import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UrlCreationOptions } from '@angular/router';
import { Product2, ProductClassification, ProductType, ProductSpecification, Company, QualityLevel, ListBrandsRequest, ListBrandsResponse, SituViewWebApiClient, Brand } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ConfigService, ProductService } from 'src/app/services';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Location } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SelectArtifactDialogComponent, SelectArtifactDialogData, SelectArtifactResultData, ArtifactViewerDialogComponent } from 'src/app/components';
import { INewProduct } from 'src/app/views';
import { HomeStatus } from 'siturenderservice-webapi-client';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CreateBrandDialogComponent, CreateBrandDialogData } from '../../create-brand-dialog/create-brand-dialog/create-brand-dialog.component';

@Component({
  selector: 'product-details-tab',
  templateUrl: './product-details-tab.component.html',
  styleUrls: ['./product-details-tab.component.scss']
})

export class ProductDetailsTabComponent extends SituBaseComponent {
  @Input() product: INewProduct = new Product2();
  @Input() productCompany: Company | undefined;
  @Input() isAddMode: boolean = false;
  @Input() readOnly: boolean = true;
  
  @Output() createProduct: EventEmitter<void> = new EventEmitter();

  productClassifications?: ProductClassification[];
  productTypes?: ProductType[];
  productSpecifications?: ProductSpecification[];
  
  readonly QualityLevel = QualityLevel;
  brandNameControl: FormControl;
  allBrands: Brand[] = [];
  companyBrands: Brand[] = [];
  filteredBrands: Observable<Brand[]>;

  selectedProductClassificationId?: string;

  selectArtifactDialogRef: MatDialogRef<SelectArtifactDialogComponent> | undefined;
  artifactViewerDialogRef: MatDialogRef<ArtifactViewerDialogComponent> | undefined;

  readonly SituViewUrl: string;

  /* Query Parameters */
  readonly CallerReferenceKeyParamName: string = "callerReferenceKey";

  private _loading = 0;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val != 0);
  }

  canNavigateBackToPreviousPage = false;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productService: ProductService,
    private configService: ConfigService,
    private situViewClient: SituViewWebApiClient,
    private dialog: MatDialog,
    private location: Location) {
    super(authService, stateService);

    this.SituViewUrl = configService.config.situViewWebApiBaseUrl;
    
    this.getProductClassifications();
    this.getProductTypes();
    this.loadBrands();
    this.getProductSpecifications();

    this.brandNameControl = new FormControl('', this.validateBrand.bind(this));

    this.filteredBrands = this.brandNameControl.valueChanges.pipe(
      startWith(this.brandNameControl.value),
      map((input) => {
        let brands = this.allBrands;
        let productCompany = this.product?.companyId || this.selectedCompany?.id;
        if (productCompany) {
          brands = brands.filter(b => b.companyId === productCompany);
        }
        if (!input) {
          return brands;
        }
        return brands.filter(b => b.name?.toLowerCase().includes(input.toLowerCase()));
      })
    );

    var previousNavigation = this.router.getCurrentNavigation()?.previousNavigation;
    if (previousNavigation) {
      var segments = previousNavigation.finalUrl?.root.children.primary.segments;
      this.canNavigateBackToPreviousPage = segments != null && segments.some(e => e.path == "products");
    }  
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.brandNameControl.setValue(this.product.brand?.name);
  }

  onClassificationChange() {
    if (this.product) {
      this.product.productTypeId = undefined;
      this.product.productSpecificationIds = [];
    }
  }

  onTypeChange() {
    if (this.product) {
      this.product.productSpecificationIds = [];
    }
  }

  onProductSpecificationSelectionChanged(selectedProductSpecificationIds: string[]) {
    if (this.product) {
      this.product.productSpecificationIds = selectedProductSpecificationIds;
    }
  }

  getProductClassifications() {
    this.loading++;
    this.productService.LoadProductClassifications()
    .then((productClassifications) => {
      if (productClassifications.length == 0) {
        this.snackBar.open("Failed to load Product Classifications.");
      }

      // Add a blank classification, for product types that are not classified
      var blankClassification = new ProductClassification({ id: undefined, name: '' });
      this.productClassifications = [blankClassification, ...productClassifications];
    }).catch((e) => {
      this.snackBar.open("Failed to load Product Classifications.");
    })
    .finally(() => {
      this.loading--;
    });
  }

  getProductTypes() {
    this.loading++;
    this.productService.LoadProductTypes()
    .then((productTypes) => {
      if (productTypes.length == 0) {
        this.snackBar.open("Failed to load Product Types.");
      }

      this.productTypes = productTypes;
      this.selectProductClassificationId();
    }).catch((e) => {
      this.snackBar.open("Failed to load Product Types.");
    })
    .finally(() => {
      this.loading--;
    });
  }

  
  getProductSpecifications() {
    this.loading++;
    this.productService.LoadProductSpecifications()
    .then((productSpecifications) => {
      if (productSpecifications.length == 0) {
        this.snackBar.open("Failed to load Product Specifications.");
      }
      this.productSpecifications = productSpecifications;
    }).catch((e) => {
      this.snackBar.open("Failed to load Product Specifications.");
    })
    .finally(() => {
      this.loading--;
    });
  }

  private loadBrands(): Promise<Brand[] | undefined> {
    this.loading++;
    return this.situViewClient.listBrands(new ListBrandsRequest())
      .then((res: ListBrandsResponse) => {
        let brands = res.brands;
        this.allBrands = brands || [];
        this.allBrands.sort((a, b) => a.name!.localeCompare(b.name!));
        return brands;
      })
      .finally(() => {
        this.loading--;
      });
  }

  public onClearBrand(): void {
    this.brandNameControl.reset();
    this.product.brandId = undefined;
    this.product.brand = undefined;
  }

  public validateBrand(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    let brands = this.allBrands;
    let productCompany = this.product?.companyId || this.selectedCompany?.id;
    if (productCompany) {
      brands = brands.filter(b => b.companyId === productCompany);
    }
    if (brands.some(b => b.name === control.value)) {
      return null;
    }
    return { invalidBrand: true }
  };

  public onBrandSelected(event: MatAutocompleteSelectedEvent): void {
    let brand = this.allBrands.find(b => b.name === event.option.value);

    if (brand) {
      this.product.brand = brand;
      this.product.brandId = brand.id;
    }
  }

  /** Auto selects brand if there's only one match in the filtered list */
  public onCheckBrandName(): void {
    if (this.brandNameControl.value?.length > 0) {
      let brands = this.allBrands;
      let productCompany = this.product?.companyId || this.selectedCompany?.id;

      if (productCompany) {
        brands = brands.filter(b => b.companyId === productCompany);
      }

      let brand = brands.filter(b => b.name?.toLowerCase().includes(this.brandNameControl?.value?.toLowerCase()));
      if (brand.length === 1) {
        this.product.brand = brand[0];
        this.brandNameControl.setValue(brand[0].name, { emitEvent: false });
        this.product.brandId = brand[0].id;
      }
    }
  }

  public onOpenAddBrands(names?: string[]): void {
    this.dialog.open<CreateBrandDialogComponent, CreateBrandDialogData, boolean>(CreateBrandDialogComponent, {
      data: { 
        brands: names, 
        companyId: this.product?.companyId || this.selectedCompany?.id
      },
      width: '80vw',
      minWidth: '350px',
      maxWidth: '1400px',
    }).afterClosed().pipe(first()).subscribe((newBrands?: boolean) => {     
      if (newBrands) {
        this.loadBrands();
      }
    });
  }

  filterTypesByClassification(): ProductType[] {
    if (this.productTypes) {
      return this.productTypes
      .filter(item => item.productClassificationId == this.selectedProductClassificationId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      return [];
    }
  }

  saveIsValid() {
    return !(this.product.id &&
    this.product.companyId &&
    this.product.name &&
    this.product.productTypeId);
  }

  async applyProduct() {
    if (this.isAddMode) {
      var saveSuccessful = await this.addProduct();

      if (saveSuccessful) {
        this.router.navigate(['products', this.product.id]);
      }
    } else {
      await this.updateProduct();
    }
  }

  async saveProduct() {
    var saveSuccessful = true;
    if (this.isAddMode) {
      saveSuccessful = await this.addProduct();
    } else {
      saveSuccessful = await this.updateProduct();
    }

    if (saveSuccessful) {
      this.navigateBack();
    }
  }

  async saveProductAndCreateAnother() {
    var saveSuccessful = await this.addProduct();

    if (saveSuccessful) {
      this.createProduct.emit();
    }
  }

  updateProduct(): Promise<boolean> {
    return this.productService.UpdateProduct(this.product as Product2)
      .then(x => {
        if (x) {
          this.showMessage(`Success! ${this.product.name} was updated.`);
          this.breadcrumbService.setParam('productSku', this.product.sku);
          return true;
        } else {
          this.showMessage(`Sorry we were unable to save your product ${this.product.name}. Please try again!`);
          return false;
        }
      })
      .catch((x: any) => {
        this.showMessage(`Sorry we were unable to save your product ${this.product.name}. Please try again!`);
        return false;
      });
  }

  addProduct(): Promise<boolean> {
    return this.productService.AddProduct(this.product as Product2)
      .then(x => {
        if (x) {
          this.showMessage(`Success! ${this.product.name} was added.`);
          return true;
        } else {
          this.showMessage(`Sorry we were unable to save your product ${this.product.name}. Please try again!`);
          return false;
        }
      })
      .catch((x: any) => {
        this.showMessage(`Sorry we were unable to save your product ${this.product.name}. Please try again!`);
        return false;
      });
  }

  assignCallerReferenceKey() {
    this.showSelectArtifactDialog(false);
  }

  bulkCreate() {
    this.showSelectArtifactDialog(true);
  }

  showSelectArtifactDialog(multiSelect: boolean) {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var dialogData = new SelectArtifactDialogData();
    dialogData.multiSelect = multiSelect;

    this.selectArtifactDialogRef = this.dialog.open(SelectArtifactDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });

    this._subscriptions.push(this.selectArtifactDialogRef.afterClosed()
      .subscribe((data: SelectArtifactResultData) => {
        if (data?.artifacts) {

          for (let i = 0; i < data.artifacts.length; i++) {
            const artifact = data.artifacts[i];
            if (i === 0) {
              this.product.callerReferenceKey = artifact.callerReferenceKey;
            } else {
              const url = this.router.serializeUrl(
                this.router.createUrlTree(['products', 'add'], { queryParams: { [this.CallerReferenceKeyParamName]: artifact.callerReferenceKey } } as UrlCreationOptions )
              );

              window.open(url, '_blank');
            }
          }
        }
      }));
  }

  onView3dPreview() {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    if (this.product?.previewArtifactId) {
      this.artifactViewerDialogRef = this.dialog.open(ArtifactViewerDialogComponent, {
        width: dialogWidth,
        height: dialogHeight,
        data: {
          fileName: "product.glb",
          artifactId: this.product.previewArtifactId
        }
      });
    }
  }

  async onGeneratePreview() {

    var response = await this.productService.GenerateProductPreview(this.product.companyId!, this.product.callerReferenceKey!);

    if (response.success) {
      if (response.status === HomeStatus.Complete) {
        this.showMessage("The Product Preview is complete!");
      } else {
        this.showMessage("Product Preview is being generated!");
      }
    } else {
      this.showMessage("Product Preview could not be generated!");
    }

  }

  navigateBack() {
    if (this.canNavigateBackToPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(['/products/catalog']);
    }
  }

  private selectProductClassificationId(): void {
    if (this.product && this.productTypes) {
      var productTypeInfo = this.productTypes.find(x => x.id === this.product?.productTypeId);
      this.selectedProductClassificationId = productTypeInfo?.productClassificationId;
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}